import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  PlayArrow as TestIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as TimeoutIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import * as apiIntegrationsService from '../../services/apiIntegrationsService';

const ConnectionTestDialog = ({ open, integration, onClose }) => {
  const [testType, setTestType] = useState('connectivity');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadTestHistory = React.useCallback(async () => {
    if (!integration?.id) return;
    
    try {
      setHistoryLoading(true);
      const history = await apiIntegrationsService.getTestHistory(integration.id, 10);
      setTestHistory(history);
    } catch (error) {
      console.error('Error loading test history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [integration?.id]);

  React.useEffect(() => {
    if (open && integration) {
      loadTestHistory();
    }
  }, [open, integration, loadTestHistory]);

  const handleTest = async () => {
    if (!integration?.id) return;

    try {
      setTesting(true);
      setTestResult(null);
      
      // Simulate user ID - in real app, get from auth context
      const userId = 'current-user-id';
      
      const result = await apiIntegrationsService.testConnection(
        integration.id,
        testType,
        userId
      );
      
      setTestResult(result);
      await loadTestHistory(); // Refresh history
    } catch (error) {
      console.error('Error testing connection:', error);
      setTestResult({
        status: 'error',
        message: error.message || 'Test failed',
        testedAt: new Date()
      });
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <SuccessIcon color="success" />;
      case 'failure':
        return <ErrorIcon color="error" />;
      case 'timeout':
        return <TimeoutIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="warning" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failure':
        return 'error';
      case 'timeout':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTestTypeDescription = (type) => {
    switch (type) {
      case 'connectivity':
        return 'Basic connectivity test to check if the API endpoint is reachable';
      case 'authentication':
        return 'Test authentication credentials and verify access';
      case 'endpoint':
        return 'Test specific API endpoints for functionality';
      case 'full':
        return 'Comprehensive test including connectivity, authentication, and endpoints';
      default:
        return 'Unknown test type';
    }
  };

  if (!integration) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: { height: '70vh' }
      }}
    >
      <DialogTitle>
        Test API Connection - {integration.name}
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Integration Details
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Base URL:</strong> {integration.baseUrl}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Type:</strong> {integration.type}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Authentication:</strong> {integration.authType === 'none' ? 'No Authentication' : integration.authType}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Environment:</strong> {integration.environment}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Run Test
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Test Type</InputLabel>
            <Select
              value={testType}
              label="Test Type"
              onChange={(e) => setTestType(e.target.value)}
              disabled={testing}
            >
              <MenuItem value="connectivity">Connectivity Test</MenuItem>
              <MenuItem value="authentication">Authentication Test</MenuItem>
              <MenuItem value="endpoint">Endpoint Test</MenuItem>
              <MenuItem value="full">Full Test</MenuItem>
            </Select>
          </FormControl>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            {getTestTypeDescription(testType)}
          </Alert>

          <Button
            variant="contained"
            startIcon={<TestIcon />}
            onClick={handleTest}
            disabled={testing}
            fullWidth
            size="large"
          >
            {testing ? 'Testing...' : 'Run Test'}
          </Button>

          {testing && <LinearProgress sx={{ mt: 2 }} />}
        </Box>

        {testResult && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Test Result
            </Typography>
            <Alert 
              severity={testResult.status === 'success' ? 'success' : 'error'}
              icon={getStatusIcon(testResult.status)}
              sx={{ mb: 2 }}
            >
              <Typography variant="body1">
                <strong>Status:</strong> {testResult.status}
              </Typography>
              <Typography variant="body2">
                <strong>Message:</strong> {testResult.message}
              </Typography>
              {testResult.responseTime && (
                <Typography variant="body2">
                  <strong>Response Time:</strong> {testResult.responseTime}ms
                </Typography>
              )}
              {testResult.statusCode && (
                <Typography variant="body2">
                  <strong>HTTP Status:</strong> {testResult.statusCode}
                </Typography>
              )}
            </Alert>
            
            {testResult.details && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Technical Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <pre style={{ 
                    backgroundColor: '#f5f5f5', 
                    padding: '10px', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(testResult.details, null, 2)}
                  </pre>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Test History
          </Typography>
          
          {historyLoading ? (
            <LinearProgress />
          ) : testHistory.length === 0 ? (
            <Alert severity="info">
              No test history available for this integration.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell align="center">Test Type</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Response Time</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {testHistory.map((test) => (
                    <TableRow key={test.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {format(test.testedAt.toDate(), 'MMM dd, yyyy HH:mm:ss')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={test.testType} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={getStatusIcon(test.status)}
                          label={test.status}
                          size="small"
                          color={getStatusColor(test.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {test.responseTime ? `${test.responseTime}ms` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {test.message}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectionTestDialog; 